interface BusinessHoursDisplayProps {
  value?: Record<string, unknown>;
  /**
   * Label for 24/7 status. Default: "24/7"
   */
  label24_7?: string;
  /**
   * Label for closed/not valid status. Default: "Closed"
   * Use "Not Valid" for promotional offers, etc.
   */
  labelClosed?: string;
}

/**
 * Display component for business hours
 * Shows regular weekly hours and special hours
 */
export function BusinessHoursDisplay({
  value,
  label24_7 = '24/7',
  labelClosed = 'Closed',
}: BusinessHoursDisplayProps) {
  if (!value) return null;

  const specialHours = value.specialHours as Array<{
    name: string;
    date: string;
    hours: { is24_7?: boolean; ranges?: Array<{ open: string; close: string }> };
  }> | undefined;

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'pm' : 'am';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${displayHour}:${minutes}${ampm}`;
  };

  const daysOfWeek = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' },
  ];

  const regularHours = daysOfWeek
    .map(({ key, label }) => {
      const dayHours = value[key] as { is24_7?: boolean; isClosed?: boolean; ranges?: Array<{ open: string; close: string }> } | undefined;
      if (!dayHours) return null;

      if (dayHours.is24_7) {
        return { day: label, hours: label24_7 };
      }
      if (dayHours.isClosed) {
        return { day: label, hours: labelClosed };
      }
      if (dayHours.ranges && Array.isArray(dayHours.ranges) && dayHours.ranges.length > 0) {
        const times = dayHours.ranges
          .map((r) => `${formatTime(r.open)} - ${formatTime(r.close)}`)
          .join(', ');
        return { day: label, hours: times };
      }
      return null;
    })
    .filter((item): item is { day: string; hours: string } => item !== null);

  return (
    <div className="space-y-2">
      {regularHours.length > 0 && (
        <div className="space-y-1">
          {regularHours.map((item, index: number) => (
            <div key={index} className="flex justify-between text-sm">
              <span className="font-medium">{item.day}:</span>
              <span className="text-muted-foreground">{item.hours}</span>
            </div>
          ))}
        </div>
      )}
      {specialHours && specialHours.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <div className="font-medium text-sm mb-2">Special Hours</div>
          {specialHours.map((special, index: number) => {
            let hours = labelClosed;
            if (special.hours.is24_7) hours = label24_7;
            else if (special.hours.ranges && special.hours.ranges.length > 0) {
              hours = special.hours.ranges
                .map((r) => `${formatTime(r.open)} - ${formatTime(r.close)}`)
                .join(', ');
            }
            return (
              <div key={index} className="flex justify-between text-sm">
                <span className="font-medium">
                  {special.name} ({new Date(special.date).toLocaleDateString()}):
                </span>
                <span className="text-muted-foreground">{hours}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
